import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { AuthContext } from "../../context/AuthContext";
import Loader from "../../components/Loader";
import { getStatusBadgeClass, truncateText } from "../../utils/Utils";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Pagination } from "../../components/ui";
import { usePagedList } from "../../hooks/usePagedList";
import { IoArrowBackSharp } from "react-icons/io5";

const AllTickets = () => {
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await axiosInstance.get(`/tickets`);
        setTickets(response.data);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [user.id]);

  const filteredTickets = tickets.filter(
    (ticket) =>
      ticket.ticket_id
        .toString()
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paged = usePagedList(filteredTickets, 25, searchTerm);

  if (loading) {
    return (
      <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
        <Loader />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1500px] space-y-5 px-4 pb-4 sm:px-6 sm:pb-6">
      <h3 className="mb-1 flex items-center gap-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
        {/* Go Back Button */}
        <button
          onClick={() => window.history.back()} // This will navigate back to the previous page
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:text-slate-400"
        >
          <IoArrowBackSharp size={24} />
        </button>
                <span className="h-6 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-red-500 to-orange-400" />
        All Tickets
      </h3>

      {/* Search Bar */}
      <div className="mb-4">
        <input
          type="text"
          className="h-11 w-full rounded-[12px] border border-gray-300 bg-white px-4 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-500/10 disabled:opacity-60 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-100 dark:placeholder:text-slate-500 dark:bg-[#1c1c1e] dark:text-white"
          placeholder="Search by ticket id, subject..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <table className="min-w-full border-separate border-spacing-0 overflow-hidden rounded-[16px] border border-gray-200 bg-white text-sm dark:border-[#2c2c2e] dark:bg-[#1c1c1e]">
        <thead>
        <tr className="bg-gray-100 dark:bg-[#161617]">
          <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">Total Tickets {tickets.length}</th>
        </tr>
        </thead>
      </table>
      {/* Table for displaying tickets */}
      <table className="min-w-full border-separate border-spacing-0 overflow-hidden rounded-[16px] border border-gray-200 bg-white text-sm dark:border-[#2c2c2e] dark:bg-[#1c1c1e]">
        <thead>
          <tr className="bg-gray-100 dark:bg-[#262628]">
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
              Ticket ID
            </th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
              Subject
            </th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
              Username
            </th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
              Status
            </th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
              Opening Date
            </th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400">
              Closing Date
            </th>
            <th className="border-b border-gray-200 bg-gray-100 px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.08em] text-gray-500 dark:border-[#2c2c2e] dark:bg-[#161617] dark:text-slate-400 text-center align-middle dark:border-[#3a3a3d]">
              View
            </th>
          </tr>
        </thead>
        <tbody>
          {paged.visible.map((ticket) => (
            <tr
              key={ticket.ticket_id}
              className="transition hover:bg-gray-50 dark:hover:bg-[#232325] dark:hover:bg-[#262628]"
            >
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                {ticket.ticket_id}
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                {truncateText(ticket.subject, 35)}
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                {ticket.username}
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                <span
                  className={`${getStatusBadgeClass(
                    ticket.status
                  )}`}
                >
                  {ticket.status}
                </span>
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                {ticket.opening_date}
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300">
                {ticket.closing_date || "N/A"}
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700 dark:border-[#2c2c2e] dark:text-slate-300 text-center align-middle dark:border-[#3a3a3d]">
                <button className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-red-500/10 hover:text-red-500 dark:hover:text-orange-400">
                  <Link to={`/admin/view-file/${ticket.file_id}`}>
                    <EyeIcon className="h-5 w-5 mr-1" />
                  </Link>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination
        page={paged.page}
        totalPages={paged.totalPages}
        onChange={paged.setPage}
        total={paged.total}
        pageSize={paged.pageSize}
      />
    </div>
  );
};

export default AllTickets;
